import { useState, useContext, useEffect } from 'react';
import {
    Button,
    Col,
    Row,
    Form,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalBody,
    FormFeedback,
} from 'reactstrap';
import { useForm } from 'react-hook-form';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import {
    CheckboxInput,
    SelectInput,
    TextInput,
} from '~/shared/components/form/Inputs';
import states from '~/shared/states.json';
import { Loading } from '~/shared/components/Loading';
import { CampaignContext } from '~/context/campaign';
import {
    fetchMyCampaign,
    createTeamMember,
    fetchTeamMember,
    putTeamMember,
} from '../../services/campaign';
import { SelectOption } from '~/types/globals';
import { fetchCampaignIsForOptions } from '~/services/options';
import toast from 'react-hot-toast';
import { ITeamMemberItem } from '../../types/campaign';

type PhoneSet = {
    id?: number;
    phone: string;
    type: string;
};

type PersonForm = {
    first_name: string;
    middle_name: string;
    last_name: string;
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    zip_code: string;
    email: string;
    phone_set: PhoneSet[];
    user?: number;
};

type TeamMemberForm = {
    id?: string;
    person: PersonForm;
    relationship_to_patient_id: string;
    role: string;
    is_primary_contact: boolean;
    is_emergency_contact: boolean;
    can_discuss_balance: boolean;
    can_request_disbursement: boolean;
};

const defaultEditTeamMember: TeamMemberForm = {
    id: '',
    person: {
        first_name: '',
        middle_name: '',
        last_name: '',
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        zip_code: '',
        email: '',
        phone_set: [{ phone: '', type: 'Cell' }],
    },
    relationship_to_patient_id: '',
    role: 'limited',
    is_primary_contact: false,
    is_emergency_contact: false,
    can_discuss_balance: false,
    can_request_disbursement: false,
};

const roleOptions = [
    {
        label: 'Limited Access',
        value: 'limited',
        description:
            'This team member has limited access and can edit all items on your Campaign Page, except changing the fundraising goal or displaying the fundraising thermometer.',
    },
    {
        label: 'Full Access',
        value: 'full',
        description:
            "In addition to editing all the items on the Campaign Page, the team member can also: access the list of contributors, add or change team members, change the client's contact information, add the client to the Find a Friend Network. <ul class='mt-2'><li>View campaign Supporters</li><li>Add or modify Team Members</li><li>Update the client's contact information</li><li>Add the client to the Find a Friend Network (New feature: coming soon!)</li></ul>",
    },
    {
        label: 'No Login',
        value: 'no',
        description:
            'The team member can access the public Campaign Page, but does not have a login to edit the Campaign Page or view the campaign Dashboard.',
    },
];

const phoneTypeOptions = [
    { label: 'Cell', value: 'Cell' },
    { label: 'Home', value: 'Home' },
    { label: 'Office', value: 'Office' },
    { label: 'Fax', value: 'Fax' },
    { label: 'Pager', value: 'Pager' },
    { label: 'Main', value: 'Main' },
    { label: 'Direct', value: 'Direct' },
];

interface OverrideModal {
    type: string;
    label: string;
    existing: string;
    new: string;
}

export const EditTeamMember = () => {
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [loading, setLoading] = useState<boolean>(true);
    const [campaign_is_forOptions, setCampaignIsForOptions] = useState<
        SelectOption[]
    >([]);
    const [emergencyContact, setEmergencyContact] =
        useState<ITeamMemberItem | null>(null);
    const [primaryContact, setPrimaryContact] =
        useState<ITeamMemberItem | null>(null);
    const [teamMember, setTeamMember] = useState(null);
    const [overrideModal, setOverrideModal] = useState<OverrideModal | null>(
        null
    );
    const history = useHistory();
    const location = useLocation();
    const { id } = useParams();
    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        getValues,
        register,
        formState: { errors, isSubmitting },
        setError,
    } = useForm({
        defaultValues: defaultEditTeamMember,
        mode: 'onChange',
    });

    const isValid = Object.values(errors).length === 0;

    const roleValue = watch('role');
    const isEmergency = watch('is_emergency_contact');
    const isPrimary = watch('is_primary_contact');
    const email = watch('person.email');
    const phoneSet = watch('person.phone_set');

    const handleCampaignResp = (resp) => {
        if (resp.detail && resp.detail === 'Invalid token.') {
            // Delete localStorage token
            localStorage.removeItem('token');
            history.push('/login');
        } else {
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
        }
    };

    const handleCampaignIsForOptionsResp = (resp) => {
        const opts = resp
            .map((opt, i) => {
                return {
                    label: opt.name,
                    value: opt.id.toString(), // Convert to string to match form value type
                };
            })
            .filter((opt) => opt.value !== '1'); // Compare as string since we converted value
        console.log('Campaign Is For Options loaded:', opts);
        setCampaignIsForOptions(opts);
    };

    const resetTeamMember = (member: ITeamMemberItem) => {
        let role = 'no';
        if (member.group === 4) {
            role = 'full';
        } else if (member.group === 5) {
            role = 'limited';
        }
        
        const relationship_to_patient = member.relationship_to_patient;
        
        // Ensure phone_set exists, fallback to empty array
        const phoneSet = member.person.phone_set && member.person.phone_set.length > 0
            ? member.person.phone_set
            : [{ phone: '', type: 'Cell' }];
        
        // Convert full state name to abbreviation (API returns "Florida", form needs "FL")
        const stateAbbr = Object.keys(states).find(
            abbr => states[abbr as keyof typeof states] === member.person.state
        ) || member.person.state || '';
        
        console.log('resetting form with member:', member);
        console.log('relationship_to_patient:', relationship_to_patient);
        console.log('relationship_to_patient_id:', relationship_to_patient?.id);
        console.log('state conversion:', member.person.state, '->', stateAbbr);
        
        reset({
            id: member.id?.toString(),
            person: {
                first_name: member.person.first_name,
                middle_name: member.person.middle_name,
                last_name: member.person.last_name,
                address_1: member.person.address_1,
                address_2: member.person.address_2,
                city: member.person.city,
                state: stateAbbr,
                zip_code: member.person.zip_code,
                email: member.person.email,
                phone_set: phoneSet,
                user: member.person.user,
            },
            relationship_to_patient_id: relationship_to_patient?.id?.toString() || '',
            role: role,
            is_primary_contact: member.is_primary_contact,
            is_emergency_contact: member.is_emergency_contact,
            can_discuss_balance: member.can_discuss_balance,
            can_request_disbursement: member.can_request_disbursement,
        });
    };

    useEffect(() => {
        if (teamMember) {
            setLoading(false);
        }
    }, [teamMember]);

    const initializeData = async () => {
        if (!campaign) {
            const promises = [fetchMyCampaign(), fetchCampaignIsForOptions()];
            const [campaignResp, campaignIsForOptionsResp] = await Promise.all(
                promises
            );
            handleCampaignResp(campaignResp);
            handleCampaignIsForOptionsResp(campaignIsForOptionsResp);
        } else {
            const campaignIsForOptionsResp = await fetchCampaignIsForOptions();
            handleCampaignIsForOptionsResp(campaignIsForOptionsResp);
        }
        if (location.state) {
            const { member, primary, emergency } = location.state;
            if (member) {
                setTeamMember(member);
                resetTeamMember(member);
            } else if (primary) {
                console.log('resetting primary contact');
                reset({
                    ...defaultEditTeamMember,
                    is_primary_contact: true,
                    role: 'full',
                });
            } else if (emergency) {
                console.log('resetting emergency');
                reset({
                    ...defaultEditTeamMember,
                    is_emergency_contact: true,
                    role: 'full',
                });
            }
            setLoading(false);
        } else if (id) {
            setLoading(true);
            const resp = await fetchTeamMember(id);

            setTeamMember(resp);
            resetTeamMember(resp);
        } else {
            setLoading(false);
        }
    };

    useEffect(() => {
        initializeData();
    }, []);

    useEffect(() => {
        if (campaign && campaign.campaignDetail && campaign.campaignDetail.team_members) {
            const { campaignDetail } = campaign;
            const existingEmergency = campaignDetail.team_members.find(
                (x) => x.is_emergency_contact
            );
            const existingPrimary = campaignDetail.team_members.find(
                (x) => x.is_primary_contact
            );
            setEmergencyContact(existingEmergency);
            setPrimaryContact(existingPrimary);
        }
    }, [campaign]);

    useEffect(() => {
        if (isEmergency && emergencyContact) {
            if (emergencyContact.id === teamMember?.id) {
                return;
            }
            const first = getValues('person.first_name');
            const last = getValues('person.last_name');
            const { person } = emergencyContact;
            let newName = first && last ? `${first} ${last}` : `this member`;
            setOverrideModal({
                type: 'is_emergency_contact',
                label: 'Emergency Contact',
                existing: `${person.first_name} ${person.last_name}`,
                new: newName,
            });
        }
    }, [isEmergency, emergencyContact, teamMember, getValues]);

    useEffect(() => {
        if (isPrimary && primaryContact) {
            if (primaryContact.id === teamMember?.id) {
                return;
            }
            const first = getValues('person.first_name');
            const last = getValues('person.last_name');
            const { person } = primaryContact;
            let newName = first && last ? `${first} ${last}` : `this member`;
            setOverrideModal({
                type: 'is_primary_contact',
                label: 'Primary Contact',
                existing: `${person.first_name} ${person.last_name}`,
                new: newName,
            });
        }
    }, [isPrimary, primaryContact, teamMember, getValues]);

    console.log('loading: ', loading);

    if (!campaign || loading) {
        return <Loading />;
    }

    const onSubmit = async (data: TeamMemberForm) => {
        console.log('data: ', data);

        if (!data.role) {
            setError('role', 'This field is required');
            return;
        }

        if ((data.role === 'full' || data.role === 'limited') && !email) {
            toast.error(
                'Team members with limited or full access must provide an email address'
            );
            return;
        }

        // Filter out empty phone entries
        if (data.person.phone_set) {
            data.person.phone_set = data.person.phone_set.filter(p => p.phone.trim() !== '');
        }

        try {
            let resp;
            let successMsg;
            const campaignId = campaign?.campaignDetail?.campaign_id;
            if (teamMember) {
                const payload: TeamMemberForm = { ...data, id: teamMember?.id };
                resp = await putTeamMember(payload, campaignId);
                successMsg = 'Team member updated';
            } else {
                resp = await createTeamMember(data, campaignId);
                successMsg = 'Team member added!';
            }

            if (resp.detail) {
                toast.error(resp.detail);
            } else {
                // Update full campaign data including application items (for Contact card status)
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                history.push('/team');
                toast.success(successMsg);
            }
        } catch (e) {
            console.log('e: ', e);
        }
    };

    const cancelOverride = () => {
        if (overrideModal?.type) {
            setValue(overrideModal.type as 'is_emergency_contact' | 'is_primary_contact', false);
        }
        setOverrideModal(null);
    };

    const closeOverride = () => {
        setOverrideModal(null);
    };

    const header = teamMember
        ? `Edit ${teamMember.person.first_name} ${teamMember.person.last_name}`
        : `Add Team Member`;

    return (
        <div className='container__inner'>
            <div className='mb-5'>
                <Row>
                    <Col md={7}>
                        <h1 className='h2'>{header}</h1>
                    </Col>
                </Row>
            </div>
            <Row>
                <Col lg='6' className='mb-5'>
                    <Form>
                        <h4>Contact Information</h4>
                        <TextInput
                            name='person.first_name'
                            label='First Name'
                            control={control}
                            required={true}
                            ariaRequired={true}
                        />
                        <TextInput
                            name='person.middle_name'
                            label='Middle Name'
                            control={control}
                        />
                        <TextInput
                            name='person.last_name'
                            label='Last Name'
                            control={control}
                            required={true}
                            ariaRequired={true}
                        />
                        <TextInput
                            name='person.address_1'
                            label='Street Address 1'
                            control={control}
                        />
                        <TextInput
                            name='person.address_2'
                            label='Street Address 2'
                            control={control}
                        />
                        <TextInput
                            name='person.city'
                            label='City'
                            control={control}
                        />
                        <Row lg='2'>
                            <Col>
                                <SelectInput
                                    control={control}
                                    name='person.state'
                                    label='State'
                                    options={Object.entries(states).map(([accronym, label]) => ({
                                        label: label,
                                        value: accronym
                                    }))}
                                />
                            </Col>
                            <Col>
                                <TextInput
                                    name='person.zip_code'
                                    label='Zip Code'
                                    type='zip'
                                    control={control}
                                />
                            </Col>
                        </Row>
                        <TextInput
                            name='person.email'
                            type='email'
                            label='Email'
                            control={control}
                            required={roleValue === 'limited' || roleValue === 'full'}
                            ariaRequired={roleValue === 'limited' || roleValue === 'full'}
                        />

                        <h5 className='mt-3'>Phone Number</h5>
                        <Row>
                            <Col md={8}>
                                <TextInput
                                    type='tel'
                                    name='person.phone_set.0.phone'
                                    label='Phone Number'
                                    control={control}
                                />
                            </Col>
                            <Col md={4}>
                                <SelectInput
                                    control={control}
                                    name='person.phone_set.0.type'
                                    label='Type'
                                    options={phoneTypeOptions}
                                />
                            </Col>
                        </Row>

                        <h4>Relationship and Role</h4>
                        <SelectInput
                            control={control}
                            required={true}
                            ariaRequired={true}
                            name='relationship_to_patient_id'
                            label='Relationship to Client'
                            options={campaign_is_forOptions}
                        />

                        <Label>Role*</Label>
                        <FormGroup check className='mb-3'>
                            {roleOptions?.map((opt, i) => {
                                return (
                                    <div key={i}>
                                        <Label check>
                                            <Input
                                                type='radio'
                                                checked={roleValue == opt.value}
                                                onChange={() => {
                                                    setValue('role', opt.value);
                                                }}
                                                name='role'
                                            />
                                            {opt.label}
                                            <div>
                                                <p
                                                    dangerouslySetInnerHTML={{
                                                        __html: opt.description,
                                                    }}
                                                />
                                            </div>
                                        </Label>
                                    </div>
                                );
                            })}
                        </FormGroup>

                        <Label>Check all that apply*</Label>
                        <FormGroup check className='mb-3'>
                            <CheckboxInput
                                name='is_primary_contact'
                                label='Primary Contact - manager of the campaign'
                                control={control}
                            />
                            <CheckboxInput
                                name='is_emergency_contact'
                                label='Emergency Contact - can make decisions on behalf of the client'
                                control={control}
                            />
                            <CheckboxInput
                                name='can_discuss_balance'
                                label='May inquire about funds raised or disbursed'
                                control={control}
                            />
                            <CheckboxInput
                                name='can_request_disbursement'
                                label='May submit fund requests on behalf of the client'
                                control={control}
                            />
                        </FormGroup>
                    </Form>
                </Col>
            </Row>
            <div
                className='sticky-bottom p-4 bg-white'
                style={{
                    borderTop: '1px solid #0D0106',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                }}
            >
                <Button
                    color='outline-dark'
                    onClick={() => history.push('/team')}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    color='primary'
                    disabled={isSubmitting || !isValid || !roleValue}
                    onClick={() => handleSubmit(onSubmit)()}
                >
                    {isSubmitting ? `Saving` : `Save`}
                </Button>
            </div>

            <Modal isOpen={!!overrideModal} toggle={cancelOverride}>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <h4>{`You already have ${overrideModal?.existing} selected as the ${overrideModal?.label}. Do you want to change it to ${overrideModal?.new}?`}</h4>
                            <Button
                                color='outline-dark'
                                onClick={cancelOverride}
                            >
                                No
                            </Button>
                            <Button
                                color='primary'
                                onClick={() => setOverrideModal(null)}
                            >
                                Yes
                            </Button>
                        </FormGroup>
                    </Form>
                </ModalBody>
            </Modal>
        </div>
    );
};
